// Протокол списков покупок: DTO сущностей и мутации.
// Один и тот же формат мутаций используется онлайн и для офлайн-очереди.
import { z } from "zod";

export const LIST_KINDS = ["grocery", "pharmacy", "kids", "other"] as const;
export type ListKind = (typeof LIST_KINDS)[number];

export const LIST_VISIBILITIES = ["family", "private"] as const;
export type ListVisibility = (typeof LIST_VISIBILITIES)[number];

export interface ListDto {
  id: string;
  name: string;
  kind: ListKind;
  ownerUserId: string;
  visibility: ListVisibility;
  isTemplate: boolean;
  status: "active" | "archived";
  version: number;
  updatedAt: string;
  deletedAt: string | null;
}

export interface ItemDto {
  id: string;
  listId: string;
  name: string;
  qty: number | null;
  unit: string | null;
  comment: string | null;
  photoFileId: string | null;
  isChecked: boolean;
  checkedById: string | null;
  position: number;
  version: number;
  updatedAt: string;
  deletedAt: string | null;
}

export interface MemberDto {
  userId: string;
  displayName: string;
  role: "admin" | "adult" | "child";
}

export interface MeDto {
  userId: string;
  language: "ru" | "uz";
  familyId: string;
  familyName: string;
  role: "admin" | "adult" | "child";
  members: MemberDto[];
  /** Токен для ссылок на фото: /api/files/<id>?t=<fileToken>. */
  fileToken: string;
  /** Разработчик (ADMIN_TELEGRAM_IDS) — видит панель разработчика. */
  isDeveloper: boolean;
}

export interface SnapshotDto {
  /** id последней записи change_log семьи; передаётся в /api/events как since. */
  cursor: string;
  me: MeDto;
  lists: ListDto[];
  items: ItemDto[];
}

const id = z.uuid();
const name = z.string().trim().min(1).max(120);
const nullableText = (max: number) => z.string().trim().max(max).nullable();

export const MutationSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("list.create"),
    id,
    name,
    kind: z.enum(LIST_KINDS),
    visibility: z.enum(LIST_VISIBILITIES).default("family"),
    isTemplate: z.boolean().default(false),
  }),
  z.object({
    type: z.literal("list.update"),
    id,
    name: name.optional(),
    kind: z.enum(LIST_KINDS).optional(),
    visibility: z.enum(LIST_VISIBILITIES).optional(),
    status: z.enum(["active", "archived"]).optional(),
  }),
  z.object({ type: z.literal("list.delete"), id }),
  z.object({
    /** Копия списка: «создать из шаблона» и «сохранить как шаблон». */
    type: z.literal("list.copy"),
    id,
    sourceId: id,
    name,
    isTemplate: z.boolean(),
    /** id исходной позиции → id новой (генерирует клиент, чтобы работать офлайн). */
    itemIds: z.record(z.string(), id),
  }),
  z.object({
    type: z.literal("item.create"),
    id,
    listId: id,
    name,
    qty: z.number().positive().max(100000).nullable().default(null),
    unit: nullableText(16).default(null),
    comment: nullableText(500).default(null),
    position: z.number().int().optional(),
  }),
  z.object({
    type: z.literal("item.update"),
    id,
    name: name.optional(),
    qty: z.number().positive().max(100000).nullable().optional(),
    unit: nullableText(16).optional(),
    comment: nullableText(500).optional(),
    isChecked: z.boolean().optional(),
    position: z.number().int().optional(),
    photoFileId: id.nullable().optional(),
  }),
  z.object({ type: z.literal("item.delete"), id }),
]);

export type Mutation = z.infer<typeof MutationSchema>;
export type MutationInput = z.input<typeof MutationSchema>;

export const MutationEnvelopeSchema = z.object({
  /** Уникальный id мутации — ключ идемпотентности при повторной отправке. */
  mutationId: z.uuid(),
  mutation: MutationSchema,
});
export type MutationEnvelope = z.infer<typeof MutationEnvelopeSchema>;

export const MutationBatchSchema = z.object({ mutations: z.array(MutationEnvelopeSchema).min(1).max(200) });

export type MutationResult =
  | { mutationId: string; ok: true; lists: ListDto[]; items: ItemDto[] }
  | { mutationId: string; ok: false; error: string };

/** Событие SSE: сущность после изменения (удаление — с заполненным deletedAt). */
export type ChangeEvent = { id: string } & ({ entity: "list"; data: ListDto } | { entity: "item"; data: ItemDto });
